import type { AppRecord } from '@core/types';
import type { RuntimeConfig } from '../auth/oauth';
import { getTokens, refreshNow } from '../auth/session';

/** An AppRecord as the API serializes it. */
export interface AppView extends AppRecord {
  /** Null when the record has no redirect URIs — only reachable via a hand-edited DynamoDB row. */
  login_url: string | null;
}

export interface Identity {
  email: string;
  brokerApp: string;
  clientId: string;
  warnings: Array<{ code: string; message: string }>;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let config: RuntimeConfig | undefined;

export function setApiConfig(next: RuntimeConfig): void {
  config = next;
}

async function parse(response: Response): Promise<any> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    // Should be unreachable: the /api/* CloudFront behavior never serves index.html. If it does
    // happen, saying so beats "Unexpected token < in JSON".
    throw new ApiError(response.status, 'BAD_RESPONSE', 'The API returned a non-JSON response.');
  }
}

async function once<T>(path: string, init: RequestInit): Promise<T> {
  const token = getTokens()?.idToken;
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.body ? { 'content-type': 'application/json' } : {}),
    },
  });

  const body = await parse(response);
  if (!response.ok) {
    const error = body?.error ?? {};
    throw new ApiError(
      response.status,
      error.code ?? 'UNKNOWN',
      error.message ?? `Request failed with ${response.status}.`,
      error.requestId,
    );
  }
  return body as T;
}

/**
 * One retry on 401, after a refresh. A second 401 means the session is genuinely over — most often
 * because the pre-token trigger refused the refresh, which is what happens when an admin is removed
 * from the Entra group.
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  try {
    return await once<T>(path, init);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401 || !config) throw err;
    await refreshNow(config);
    return once<T>(path, init);
  }
}

export const api = {
  config: () => apiFetch<RuntimeConfig>('/api/config'),
  me: () => apiFetch<Identity>('/api/me'),
  list: () => apiFetch<{ apps: AppView[] }>('/api/apps').then((r) => r.apps),
  get: (identifier: string) =>
    apiFetch<{ app: AppView }>(`/api/apps/${encodeURIComponent(identifier)}`).then((r) => r.app),
  create: (input: { appName: string; redirectUris: string[]; requiredGroupIds?: string[] }) =>
    apiFetch<{ app: AppView }>('/api/apps', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.app),
  update: (
    identifier: string,
    input: { redirectUris?: string[]; requiredGroupIds?: string[] },
  ) =>
    apiFetch<{ app: AppView }>(`/api/apps/${encodeURIComponent(identifier)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }).then((r) => r.app),
  setEnabled: (identifier: string, enabled: boolean) =>
    apiFetch<{ app: AppView }>(
      `/api/apps/${encodeURIComponent(identifier)}/${enabled ? 'enable' : 'disable'}`,
      { method: 'POST' },
    ).then((r) => r.app),
  // The server re-checks this value against the resolved record, so the typed confirmation is a
  // contract rather than a UI formality.
  remove: (identifier: string, confirm: string) =>
    apiFetch<{ app: AppView }>(
      `/api/apps/${encodeURIComponent(identifier)}?confirm=${encodeURIComponent(confirm)}`,
      { method: 'DELETE' },
    ).then((r) => r.app),
};
